import { useMemo, useState } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Chip,
  Grid,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Tooltip,
} from '@mui/material';
import PropTypes from 'prop-types';
import { useFirestore } from 'react-redux-firebase';
import collections from '../../../../constants/FirestoreCollections';
import { useDispatch, useSelector } from 'react-redux';
import { actions } from '../../../../actions';
import RichTextEditor from './RichTextEditor';

const EmailConfig = ({ previewModalOpen, scrollbarStyles }) => {
  const firestore = useFirestore();
  const organization = useSelector((state) => state.app.organization);
  const organizationsList = useSelector((state) => state.app.organizations);
  const prestoConfig = useSelector((state) => state.app.prestobotsConfig);
  const appDispatch = useDispatch();

  const memorizedValues = useMemo(() => {
    const billingEmails = organization?.configuration?.billingEmails;
    const globalBillingEmailsConfig = prestoConfig?.billingEmails;

    return {
      bankAccount: billingEmails?.bankAccount || {},
      emails: billingEmails?.emails || [],
      frequentEmails: billingEmails?.frequentEmails || [],
      prestoBankAccounts: globalBillingEmailsConfig?.bankAccount || [],
      personalizedText: billingEmails?.personalizedText || '',
      combinedBillOrg: billingEmails?.combinedBillOrg || {},
    };
  }, [organization?.configuration?.billingEmails, prestoConfig?.billingEmails]);

  const {
    bankAccount,
    prestoBankAccounts,
    emails,
    frequentEmails,
    personalizedText: customizedText,
    combinedBillOrg,
  } = memorizedValues;

  const [email, setEmail] = useState('');
  const [selectedRecipient, setSelectedRecipient] = useState('');
  const [selectedBankAccount, setSelectedBankAccount] = useState(bankAccount);
  const [selectedCombinedOrg, setSelectedCombinedOrg] = useState(combinedBillOrg);
  const [personalizedText, setPersonalizedText] = useState(customizedText);
  const [isEmailValid, setIsEmailValid] = useState(false);

  const handleEmailChange = (e) => {
    const newEmail = e.target.value;
    setEmail(newEmail);
    setIsEmailValid(isValidEmail(newEmail));
  };

  const isValidEmail = (email) => {
    const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailPattern.test(email);
  };

  const updateFirestoreData = async (updateData) => {
    try {
      await firestore.collection(collections.organizations).doc(organization.id).update(updateData);
    } catch (error) {
      appDispatch({
        type: actions.alert,
        alert: {
          snackbarOpen: true,
          snackbarMessage: `Ocurrio un error, intente nuevamente`,
          snackbarSeverity: 'error',
        },
      });
      console.error('updateFirestoreData', error);
      throw error;
    }
  };

  const handleAddEmail = async (field, value) => {
    if (value) {
      try {
        const updateData = {
          [`configuration.billingEmails.${field}`]: firestore.FieldValue.arrayUnion(value),
        };
        await updateFirestoreData(updateData);
        field === 'emails' && setEmail('');
        field === 'frequentEmails' && setSelectedRecipient('');
        appDispatch({
          type: actions.alert,
          alert: {
            snackbarOpen: true,
            snackbarMessage: `Email agregado exitosamente`,
            snackbarSeverity: 'success',
          },
        });
      } catch (error) {
        console.error('handleAddEmail ', error);
      }
    }
  };

  const handleDeleteEmail = async (field, value) => {
    try {
      const updatedEmails = emails?.filter((e) => e !== value);
      const updatedFrequentEmails = frequentEmails?.filter((e) => e !== value);
      const updateData = {};
      if (field === 'emails') {
        updateData[`configuration.billingEmails.emails`] = updatedEmails;
        updateData['configuration.billingEmails.frequentEmails'] = updatedFrequentEmails;
      } else {
        updateData['configuration.billingEmails.frequentEmails'] = updatedFrequentEmails;
      }
      await updateFirestoreData(updateData);
      appDispatch({
        type: actions.alert,
        alert: {
          snackbarOpen: true,
          snackbarMessage: `Email eliminado exitosamente`,
          snackbarSeverity: 'success',
        },
      });
    } catch (error) {
      console.error('handleDeleteEmail', error);
    }
  };

  const handleSelectCombinedOrganization = async (combinedOrg) => {
    if (combinedOrg) {
      try {
        const updateData = {
          'configuration.billingEmails.combinedBillOrg': combinedOrg,
        };
        await updateFirestoreData(updateData);
        appDispatch({
          type: actions.alert,
          alert: {
            snackbarOpen: true,
            snackbarMessage: `Factura combinada exitosamente`,
            snackbarSeverity: 'success',
          },
        });
      } catch (error) {
        console.error('handleSelectCombinedOrganization ', error);
      }
    }
  };
  const handleSelectBankAccount = async (bankAccount) => {
    if (bankAccount) {
      try {
        const updateData = {
          'configuration.billingEmails.bankAccount': bankAccount,
        };
        await updateFirestoreData(updateData);
        appDispatch({
          type: actions.alert,
          alert: {
            snackbarOpen: true,
            snackbarMessage: `Cuenta bancaria actualizada`,
            snackbarSeverity: 'success',
          },
        });
      } catch (error) {
        console.error('handleSelectBankAccount ', error);
      }
    }
  };
  const handlePersonalizedText = async () => {
    if (personalizedText) {
      try {
        const updateData = {
          'configuration.billingEmails.personalizedText': personalizedText,
        };
        await updateFirestoreData(updateData);
        appDispatch({
          type: actions.alert,
          alert: {
            snackbarOpen: true,
            snackbarMessage: `Texto actualizado`,
            snackbarSeverity: 'success',
          },
        });
      } catch (error) {
        console.error('handlePersonalizedText ', error);
      }
    }
  };

  return (
    <Box
      sx={{
        width: !previewModalOpen ? '100%' : '400px',
        display: 'flex',
        flexDirection: 'column',
        maxWidth: '535px',
        minWidth: '400px',
        height: '100%',
        padding: 1,
        marginBottom: '20px',
        ...scrollbarStyles,
      }}
    >
      <Typography variant="h6" sx={{ mb: 2, textAlign: 'center' }}>
        Configuración
      </Typography>
      <Box sx={{ marginBottom: 3 }}>
        <Typography variant="h6">Emails Guardados:</Typography>
        <Grid container spacing={1} sx={{ mb: 1.5 }}>
          {emails?.map((emailChip, index) => (
            <Grid item key={index}>
              <Chip
                label={emailChip}
                color="primary"
                sx={{ borderRadius: 1 }}
                onDelete={() => handleDeleteEmail('emails', emailChip)}
              />
            </Grid>
          ))}
        </Grid>
        <Box>
          <TextField
            fullWidth
            variant="outlined"
            label="Agregar Email"
            value={email}
            onChange={handleEmailChange}
            sx={{ marginBottom: 2 }}
          />
          <Tooltip
            title="El correo debe tener formato @dominio."
            arrow
            disableHoverListener={isEmailValid}
          >
            <span>
              <Button
                variant="contained"
                color="primary"
                fullWidth
                onClick={() => handleAddEmail('emails', email)}
                disabled={!isEmailValid}
              >
                Agregar
              </Button>
            </span>
          </Tooltip>
        </Box>
      </Box>
      <Box sx={{ marginBottom: 3 }}>
        <Typography variant="h6">Destinatarios Frecuentes:</Typography>
        <Grid container spacing={1} sx={{ mb: 1.5 }}>
          {frequentEmails?.map((frequentEmail, index) => (
            <Grid item key={index}>
              <Chip
                label={frequentEmail}
                color="secondary"
                sx={{ borderRadius: 1 }}
                onDelete={() => handleDeleteEmail('frequentEmails', frequentEmail)}
              />
            </Grid>
          ))}
        </Grid>
        <FormControl fullWidth variant="outlined">
          <InputLabel>Destinatarios Frecuentes</InputLabel>
          <Select
            value={selectedRecipient}
            onChange={(e) => setSelectedRecipient(e.target.value)}
            label="Email Frecuente"
          >
            {emails
              ?.filter((email) => !frequentEmails?.includes(email))
              ?.map((email, index) => (
                <MenuItem key={index} value={email}>
                  {email}
                </MenuItem>
              ))}
          </Select>
        </FormControl>
        <Button
          variant="contained"
          color="primary"
          fullWidth
          sx={{ mt: 2 }}
          onClick={() => handleAddEmail('frequentEmails', selectedRecipient)}
        >
          Agregar a Frecuentes
        </Button>
      </Box>
      <Box sx={{ marginBottom: 3 }}>
        <Box>
          <Typography variant="h6">Cuenta Bancaria Seleccionada:</Typography>
          {prestoBankAccounts?.map((bank, index) =>
            bank?.bankName === selectedBankAccount?.bankName ? (
              <Chip
                key={index}
                label={bank.bankName}
                color="secondary"
                sx={{ borderRadius: 1, mb: 1.7, width: '80%', ml: '10%' }}
              />
            ) : null,
          )}
        </Box>
        <FormControl fullWidth variant="outlined">
          <InputLabel>Cuenta Bancaria</InputLabel>
          <Select
            value={selectedBankAccount?.bankName || ''}
            onChange={(e) => {
              setSelectedBankAccount(
                prestoBankAccounts?.find((bank) => bank?.bankName === e.target.value),
              );
              handleSelectBankAccount(
                prestoBankAccounts?.find((bank) => bank?.bankName === e.target.value),
              );
            }}
            label="Cuenta Bancaria"
          >
            {prestoBankAccounts?.map((bank, index) => (
              <MenuItem color="secondary" key={index} value={bank.bankName}>
                {bank?.bankName}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>
      <Box sx={{ marginBottom: 3 }}>
        <Typography variant="h6" sx={{ marginBottom: 1 }}>
          Texto Personalizado:
        </Typography>
        <Box>
          <RichTextEditor
            personalizedText={personalizedText}
            setPersonalizedText={setPersonalizedText}
          />
          <Button
            variant="contained"
            color="primary"
            fullWidth
            onClick={() => handlePersonalizedText()}
            sx={{ marginTop: 2 }}
          >
            Guardar texto personalizado
          </Button>
        </Box>
      </Box>
      <Box sx={{ marginBottom: 3 }}>
        <Box>
          <Typography variant="h6">Facturación combinada con:</Typography>
          {organizationsList?.map((org, index) =>
            org?.displayName === selectedCombinedOrg?.displayName ? (
              <Chip
                key={index}
                label={org.displayName}
                color="secondary"
                sx={{ borderRadius: 1, mb: 1.7, width: '80%', ml: '10%' }}
              />
            ) : null,
          )}
        </Box>
        <FormControl fullWidth variant="outlined">
          <InputLabel>Combinar con</InputLabel>
          <Select
            value={selectedCombinedOrg?.displayName || ''}
            onChange={(e) => {
              const selectedOrg = organizationsList?.find(
                (org) => org?.displayName === e.target.value,
              );
              if (selectedOrg) {
                setSelectedCombinedOrg({
                  id: selectedOrg.id,
                  displayName: selectedOrg.displayName,
                  templates: selectedOrg.templates,
                });
                handleSelectCombinedOrganization({
                  id: selectedOrg.id,
                  displayName: selectedOrg.displayName,
                  templates: selectedOrg.templates,
                });
              }
            }}
            label="Cuenta Bancaria"
          >
            {organizationsList
              ?.filter(
                (org) => org.displayName !== organization.displayName && org.disabled !== true,
              )
              ?.map((org, index) => (
                <MenuItem color="secondary" key={index} value={org.displayName}>
                  {org?.displayName}
                </MenuItem>
              ))}
          </Select>
        </FormControl>
        <Button
          variant="contained"
          color="primary"
          fullWidth
          onClick={() => {
            setSelectedCombinedOrg({});
            handleSelectCombinedOrganization({});
          }}
          sx={{ marginTop: 2 }}
          disabled={!selectedCombinedOrg}
        >
          Remover facturación combinada
        </Button>
      </Box>
    </Box>
  );
};
EmailConfig.propTypes = {
  previewModalOpen: PropTypes.bool,
  scrollbarStyles: PropTypes.any,
};

export default EmailConfig;
